import { QuestInstance } from "@joonapp/web-shared"
import { UserRole } from "@joonapp/web-shared"
import { captureException } from "@sentry/react"
import { AxiosResponse } from "axios"
import { RRule } from "rrule"

import { createJoonAPIClient } from "../../util/joon-api"
import {
  convertTo24HourFormat,
  getRoutineFromTimeString,
} from "../../util/time"

export const getAssignedQuests = async (
  selectedDate: string,
  userIds: number[]
): Promise<QuestInstance[]> => {
  const API = createJoonAPIClient()

  try {
    const questPromises = userIds.map((id) =>
      API.get<{ results: QuestInstance[] }>(`api/quest-instances/assigned/`, {
        params: {
          date: selectedDate,
          user_id: id,
          role: UserRole.COACH,
        },
      })
    )

    const responses = await Promise.allSettled(questPromises)

    return responses
      .filter((res) => res.status === "fulfilled")
      .flatMap(
        (res) =>
          (
            res as unknown as PromiseFulfilledResult<
              AxiosResponse<{ results: QuestInstance[] }>
            >
          ).value.data.results
      )
  } catch (error) {
    captureException(error)
    throw error
  }
}

export const addQuests = async ({
  questInfo,
  userIds,
}: {
  questInfo: {
    title: string
    description?: string
    time: string
    date: string
    resourceId: number | null | undefined
  }
  userIds: number[]
}) => {
  const API = createJoonAPIClient()

  try {
    const recurrence = new RRule({
      freq: RRule.DAILY,
      byweekday: undefined,
      interval: undefined,
    }).toString()

    const questPromises = userIds.map((id) =>
      API.post(`api/quest-series/`, {
        title: questInfo.title,
        description: questInfo.description,
        reminder_time: questInfo.time,
        start_date: questInfo.date,
        end_date: questInfo.date,
        occurrence_limit: 1,
        user_id: id,
        recurrence,
        role: UserRole.COACH,
        redeemable_reward: 10,
        routine: getRoutineFromTimeString(
          convertTo24HourFormat(questInfo.time)
        ),
        resource_id: questInfo.resourceId,
      })
    )

    const responses = await Promise.allSettled(questPromises)

    return Promise.all(responses)
  } catch (error) {
    captureException(error)
    throw error
  }
}

export const editQuests = async ({
  questInfo,
  userIds,
}: {
  questInfo: {
    seriesId: number
    title: string
    description?: string
    time: string
    date: string
    resourceId: number | null | undefined // null to delete resource
  }
  userIds: number[]
}) => {
  const API = createJoonAPIClient()

  try {
    const questPromises = userIds.map((id) =>
      API.patch(`api/quest-series/${questInfo.seriesId}/`, {
        title: questInfo.title,
        description: questInfo.description,
        reminder_time: questInfo.time,
        start_date: questInfo.date,
        occurrence_limit: 1,
        end_date: questInfo.date,
        user_id: id,
        role: UserRole.COACH,
        routine: getRoutineFromTimeString(
          convertTo24HourFormat(questInfo.time)
        ),
        resource_id: questInfo.resourceId,
      })
    )

    const responses = await Promise.allSettled(questPromises)

    return Promise.all(responses)
  } catch (error) {
    captureException(error)
    throw error
  }
}

export async function deleteQuest(questSeriesId: number) {
  const API = createJoonAPIClient()
  try {
    return API.delete(`api/quest-series/${questSeriesId}/`, {
      params: { role: UserRole.COACH },
    })
  } catch (error) {
    captureException(error)
    throw error
  }
}
